import React from "react";
import ReactQueryParams from "react-query-params";
import apiProxy from "../../api/apiProxy";
import StoreDetailsPage from "./StoreDetailsPage";
import PageTitle from "../PageTitle";
import LoadingWrapper from "../LoadingWrapper";
import * as lightingSubscriptions from "../../utils/lightingSubscriptions";
import { timer } from "rxjs";
import moment from "moment";

class StoreDetailsPageWrapper extends ReactQueryParams {
  state = {
    store: {},
    devices: [],
    isLoading: true,
    isLoadingDevices: false,
    storeStatus: null,
    showLightingPanel: false,
    tagHierarchy: {
      "submeter-category": [{}]
    },
    energyLoadError: null,
    lightingLoadError: null,
    scenes: [],
    openingHours: {},
    overrides: {},
    storeConfiguration: {},
    zoneConfiguration: [],
    subscriptions: []
  };

  componentDidMount() {
    const storeId = this.props.storeId;

    this.loadDetails(storeId);
  }

  componentWillUnmount() {
    if (this.state.subscriptions && this.state.subscriptions.length > 0) {
      this.state.subscriptions.forEach(sub => {
        sub.value.unsubscribe();
      });
    }
  }

  handleTabChange = tabKey => {
    this.setQueryParams({
      tab: tabKey
    });
  };

  loadDetails = storeId => {
    this.setState({ energyLoadError: null, lightingLoadError: null });

    this.loadEnergyStoreDetails(storeId)
      .catch(error => {
        this.setState({ energyLoadError: error });
      })
      .then(() => this.loadLightingStoreDetails(storeId))
      .catch(error => {
        this.setState({ lightingLoadError: error });

        const allFailed =
          this.state.energyLoadError && this.state.lightingLoadError;

        if (allFailed) {
          // Prioritise non-404 errors, so we know there is an issue
          if (this.state.energyLoadError.status !== 404) {
            throw this.state.energyLoadError;
          }
          if (this.state.lightingLoadError.status !== 404) {
            throw this.state.lightingLoadError;
          }

          throw error;
        }
      })
      .then(() => {
        this.setState({ isLoading: false });
      })
      .catch(error => this.props.onError(error));
  };

  loadEnergyStoreDetails = storeId => {
    let fetchDevicesResponse = {};

    return this.fetchEnergyStore(storeId)
      .then(response => {
        this.setState({ store: response });

        return this.fetchDevices(storeId);
      })
      .then(response => {
        fetchDevicesResponse = response;

        return this.fetchLatestSensorDataForStore(storeId);
      })
      .then(response => {
        this.setState({
          devices: this.mapDevices(fetchDevicesResponse.devices, response.data),
          latestSensorData: response.data,
          tagHierarchy: fetchDevicesResponse.tagHierarchy
        });
      });
  };

  loadLightingStoreDetails = storeId =>
    this.fetchLightingStore(storeId)
      .then(response => {
        this.setState({
          store: response.store,
          storeConfiguration: response.storeConfiguration,
          zoneConfiguration: response.zoneConfiguration
        });

        this.applyLiveUpdatesForLightingStore(storeId);

        return apiProxy.storeLighting.getStoreStatus(storeId).then(response => {
          this.setState({ storeStatus: response });

          this.applyLiveUpdatesForStoreStatus(storeId);
        });
      })
      .then(() =>
        this.fetchOpeningHours(storeId).then(response => {
          this.setState({ openingHours: response });
          this.applyLiveUpdatesForOpeningHours(storeId);
        })
      )
      .then(() =>
        this.fetchCurrentOverridesForStore(storeId).then(response => {
          this.setState({ overrides: response });
          this.applyLiveUpdatesForOverrides(storeId);
        })
      );

  getLightingStore = storeId => {
    apiProxy.storeLighting
      .getStore(storeId)
      .then(response => {
        this.setState({
          zoneConfiguration: response.zoneConfiguration
        });
      })
      .catch(error => {
        if (error) {
          const subscription = this.state.subscriptions.find(
            x => x.name === lightingSubscriptions.SUBSCRIPTION_LIGHTING_STORE
          );
          if (subscription) {
            this.state.subscription.value.unsubscribe();
          }
          this.props.addErrorBanner(
            "The scene levels could not be updated. The data on this page may be out of date."
          );
        }
      });
  };

  getStoreStatus = storeId => {
    apiProxy.storeLighting
      .getStoreStatus(storeId)
      .then(response => {
        this.setState({
          storeStatus: response
        });
      })
      .catch(error => {
        if (error) {
          const subscription = this.state.subscriptions.find(
            x => x.name === lightingSubscriptions.SUBSCRIPTION_STORE_STATUS
          );
          if (subscription) {
            this.state.subscription.value.unsubscribe();
          }
          this.props.addErrorBanner(
            "The commissioning status could not be updated. The data on this page may be out of date."
          );
        }
      });
  };

  getStoreOpeningHours = storeId => {
    this.fetchOpeningHours(storeId)
      .then(response => {
        this.setState({ openingHours: response });
      })
      .catch(error => {
        if (error) {
          const subscription = this.state.subscriptions.find(
            x => x.name === lightingSubscriptions.SUBSCRIPTION_OPENING_HOURS
          );
          if (subscription) {
            this.state.subscription.value.unsubscribe();
          }
          this.props.addErrorBanner(
            "The opening hours could not be updated. The data on this page may be out of date."
          );
        }
      });
  };

  getStoreOverrides = storeId => {
    this.fetchCurrentOverridesForStore(storeId)
      .then(response => {
        this.setState({ overrides: response });
      })
      .catch(error => {
        if (error) {
          const subscription = this.state.subscriptions.find(
            x => x.name === lightingSubscriptions.SUBSCRIPTION_OVERRIDES
          );
          if (subscription) {
            this.state.subscription.value.unsubscribe();
          }
          this.props.addErrorBanner(
            "The overrides could not be updated. The data on this page may be out of date."
          );
        }
      });
  };

  applyLiveUpdatesForStoreStatus = storeId => {
    const storeStatusSubscription = timer(0, 30000).subscribe(() => {
      this.getStoreStatus(storeId);
    });

    const subscriptions = this.state.subscriptions;
    subscriptions.push({
      name: lightingSubscriptions.SUBSCRIPTION_STORE_STATUS,
      value: storeStatusSubscription
    });

    this.setState({ subscriptions: subscriptions });
  };

  applyLiveUpdatesForLightingStore = storeId => {
    const lightingStoreSubscription = timer(0, 30000).subscribe(() => {
      this.getLightingStore(storeId);
    });

    const subscriptions = this.state.subscriptions;
    subscriptions.push({
      name: lightingSubscriptions.SUBSCRIPTION_LIGHTING_STORE,
      value: lightingStoreSubscription
    });

    this.setState({ subscriptions: subscriptions });
  };

  applyLiveUpdatesForOverrides = storeId => {
    const overridesSubscription = timer(0, 30000).subscribe(() => {
      this.getStoreOverrides(storeId);
    });

    const subscriptions = this.state.subscriptions;
    subscriptions.push({
      name: lightingSubscriptions.SUBSCRIPTION_OVERRIDES,
      value: overridesSubscription
    });

    this.setState({ subscriptions: subscriptions });
  };

  applyLiveUpdatesForOpeningHours = storeId => {
    const openingHoursSubscription = timer(0, 30000).subscribe(() => {
      this.getStoreOpeningHours(storeId);
    });

    const subscriptions = this.state.subscriptions;
    subscriptions.push({
      name: lightingSubscriptions.SUBSCRIPTION_OPENING_HOURS,
      value: openingHoursSubscription
    });

    this.setState({ subscriptions: subscriptions });
  };

  handleFilterChange = filter => {
    this.setQueryParams({
      "submeter-category": filter.category
        ? encodeURIComponent(filter.category)
        : undefined,
      "submeter-purpose": filter.purpose
        ? encodeURIComponent(filter.purpose)
        : undefined,
      "submeter-sub-purpose": filter.subpurpose
        ? encodeURIComponent(filter.subpurpose)
        : undefined
    });
  };

  loadDevices = storeId => {
    let fetchDevicesResponse = {};

    this.setState({ isLoadingDevices: true });

    this.fetchDevices(storeId)
      .then(response => {
        fetchDevicesResponse = response;

        return this.fetchLatestSensorDataForStore(storeId);
      })
      .then(response => {
        this.setState({
          devices: this.mapDevices(fetchDevicesResponse.devices, response.data),
          latestSensorData: response.data,
          tagHierarchy: fetchDevicesResponse.tagHierarchy,
          isLoadingDevices: false
        });
      })
      .catch(response => {
        this.setState({ energyLoadFailed: response });
      })
      .finally(() => {
        this.setState({ isLoadingDevices: false });
      });
  };

  mapSensorsToDeviceSensors() {
    if (this.state.devices == null) return;

    const sensors = [];
    this.state.devices.forEach(device => {
      if (device.primarySensor == null) return;
      sensors.push(device.primarySensor);
    });

    return sensors;
  }

  fetchEnergyStore = storeId => apiProxy.store.getStore(storeId);

  fetchLatestSensorDataForStore = storeId =>
    apiProxy.store.getLatestSensorDataForStore(storeId);

  fetchDevices = storeId => {
    const category = decodeURIComponent(this.queryParams["submeter-category"]);
    const purpose = decodeURIComponent(this.queryParams["submeter-purpose"]);
    const subpurpose = decodeURIComponent(
      this.queryParams["submeter-sub-purpose"]
    );

    return apiProxy.store.getDevices(storeId, category, purpose, subpurpose);
  };

  fetchLightingStore = storeId => apiProxy.storeLighting.getStore(storeId);

  fetchOpeningHours = storeId => {
    const date = new moment().format("YYYYMMDD000000");
    return apiProxy.storeLighting.getOpeningHours(storeId, date);
  };

  fetchCurrentOverridesForStore = storeId => {
    return apiProxy.storeLighting.getCurrentOverridesForStore(storeId);
  };

  handleSubmit = () => {
    const storeId = this.props.storeId;
    return apiProxy.storeLighting.disableManagersOverride(storeId);
  };

  handleSaveComplete = () => {
    const storeId = this.props.storeId;
    this.getStoreStatus(storeId);
    this.props.toastManager.monitorManagersOverrideState(storeId);
  };

  mapDevices = (devices, latestSensorData) => {
    const mappedDevices = devices.map(d => {
      let primarySensor = null;
      if (d.primarySensor) {
        const sensorId = d.primarySensor.dingoId.split("-")[1];

        const sensorData = latestSensorData.find(
          sd => sd.sensorId === sensorId
        );

        let latestSensorDataForPrimarySensor = null;

        if (sensorData) {
          latestSensorDataForPrimarySensor = {
            time: sensorData.data.values[0][0],
            value: sensorData.data.values[0][1]
          };
        }

        primarySensor = {
          ...d.primarySensor,
          latestSensorData: latestSensorDataForPrimarySensor
        };
      }

      return {
        ...d,
        primarySensor: primarySensor
      };
    });

    return mappedDevices;
  };

  mapZoneConfiguration = (store, scenes) => {
    if (store.storeConfiguration != null) {
      return store.storeConfiguration.zones.map(zone => {
        return {
          zone: {
            name: zone.zone.name,
            zoneId: zone.zone.zoneId,
            zoneType: zone.zone.zoneType
          },

          scenes: this.mapScenes(store.zoneConfiguration, zone, scenes)
        };
      });
    }
  };

  mapScenes = (zoneConfigurationToMap, zone, scenes) => {
    const zoneConfiguration = zoneConfigurationToMap.find(
      zoneConfiguration => zoneConfiguration.zone.zoneId === zone.zone.zoneId
    );
    if (!zoneConfiguration) {
      return scenes.scenes.map(scene => {
        return {
          scene: {
            name: scene.name,
            description: scene.description
          },
          sceneLevel: null
        };
      });
    }
  };

  activeTab() {
    return this.queryParams.tab ? this.queryParams.tab : "energy";
  }

  render() {
    return (
      <PageTitle
        title={
          this.state.isLoading
            ? ""
            : this.state.store.name + " " + this.state.store.storeId
        }
      >
        <LoadingWrapper isLoading={this.state.isLoading}>
          <StoreDetailsPage
            queryParams={this.queryParams}
            storeId={this.props.storeId}
            store={this.state.store}
            isLoading={this.state.isLoading}
            sensors={this.mapSensorsToDeviceSensors()}
            handleFilterChange={this.handleFilterChange}
            loadDevices={this.loadDevices}
            devices={this.state.devices}
            tagHierarchy={this.state.tagHierarchy}
            isLoadingDevices={this.state.isLoadingDevices}
            storeStatus={this.state.storeStatus}
            submeterCategory={decodeURIComponent(
              this.queryParams["submeter-category"]
            )}
            submeterPurpose={decodeURIComponent(
              this.queryParams["submeter-purpose"]
            )}
            submeterSubpurpose={decodeURIComponent(
              this.queryParams["submeter-sub-purpose"]
            )}
            showLightingPanel={this.state.showLightingPanel}
            energyLoadFailed={
              this.state.energyLoadError &&
              this.state.energyLoadError.status !== 404
            }
            lightingLoadFailed={
              this.state.lightingLoadError &&
              this.state.lightingLoadError.status !== 404
            }
            onTabChange={this.handleTabChange}
            activeTab={this.activeTab()}
            storeConfiguration={this.state.storeConfiguration}
            zoneConfiguration={this.state.zoneConfiguration}
            openingHours={this.state.openingHours}
            overrides={this.state.overrides}
            onSubmit={this.handleSubmit}
            onSaveComplete={() => this.handleSaveComplete()}
          />
        </LoadingWrapper>
      </PageTitle>
    );
  }
}

export default StoreDetailsPageWrapper;
